<template>
  <div class="report-page">
    <ReportHeaderTab />

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Daily Sales Report</h5>
        <p class="page-sub">Track sales performance across dates, stores and payment types</p>
      </div>

      <!-- Filters -->
      <div class="filters-wrap">

        <!-- Date range -->
        <div class="filter-group">
          <div class="filter-label">From</div>
          <div class="date-input-wrap">
            <font-awesome-icon icon="calendar" class="date-icon" />
            <Datepicker
              v-model="tableData.start_date"
              :enableTimePicker="false"
              class="date-picker"
            />
          </div>
        </div>

        <div class="filter-sep">—</div>

        <div class="filter-group">
          <div class="filter-label">To</div>
          <div class="date-input-wrap">
            <font-awesome-icon icon="calendar" class="date-icon" />
            <Datepicker
              v-model="tableData.end_date"
              :enableTimePicker="false"
              class="date-picker"
            />
          </div>
        </div>

        <!-- Store -->
        <div class="filter-group">
          <div class="filter-label">Store</div>
          <Multiselect
            v-model="tableData.store"
            label="name"
            :options="getStore"
            placeholder="All Stores"
            :searchable="true"
            :create-option="false"
            :close-on-select="true"
            class="filter-multiselect"
          />
        </div>

        <!-- Payment type -->
        <div class="filter-group">
          <div class="filter-label">Payment</div>
          <select v-model="tableData.payment_type" class="filter-select">
            <option v-for="pay in paymentTypes" :key="pay.id" :value="pay.value">
              {{ pay.name }}
            </option>
          </select>
        </div>

        <!-- Submit -->
        <button class="btn-apply" :disabled="crudLoading" @click="fetchData()">
          <font-awesome-icon :icon="crudLoading ? 'spinner' : 'filter'" :spin="crudLoading" />
          {{ crudLoading ? 'Applying...' : 'Apply' }}
        </button>

        <!-- Export -->
        <div class="dropdown" id="exportDrop">
          <button class="btn-outline" @click="toggleExport">
            <font-awesome-icon icon="download" />
            Export
          </button>
          <ul class="dropdown-menu dropdown-menu-end" :class="{ show: exportOpen }">
            <li>
              <a class="dropdown-item" href="#" @click.prevent="exportPdf">
                <font-awesome-icon icon="file-pdf" class="text-danger me-2" /> PDF
              </a>
            </li>
            <li>
              <a class="dropdown-item" href="#" @click.prevent="reusableMixinToCSV('Daily-sales-report')">
                <font-awesome-icon icon="file-csv" class="text-success me-2" /> CSV
              </a>
            </li>
          </ul>
        </div>

      </div>
    </div>

    <!-- Summary cards -->
    <div class="summary-grid" v-if="details.length > 0 && !loading">
      <div class="summary-card">
        <div class="summary-label">Transactions</div>
        <div class="summary-value">{{ total_transaction }}</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">Total Amount</div>
        <div class="summary-value">{{ company?.currency?.symbol }}{{ $filters.formatMoney(total_amount) }}</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">Total Cost</div>
        <div class="summary-value">{{ company?.currency?.symbol }}{{ $filters.formatMoney(total_cost) }}</div>
      </div>
      <div class="summary-card" :class="total_profit < 0 ? 'summary-card--loss' : 'summary-card--profit'">
        <div class="summary-label">Total Profit</div>
        <div class="summary-value">
          <font-awesome-icon :icon="total_profit < 0 ? 'arrow-down' : 'arrow-up'" class="profit-arrow" />
          {{ company?.currency?.symbol }}{{ $filters.formatMoney(total_profit) }}
        </div>
      </div>
    </div>

    <!-- Data card -->
    <div class="data-card">

      <!-- Loading -->
      <div v-if="loading" class="state-block">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3 text-muted-cell">Loading sales data...</p>
      </div>

      <!-- Empty -->
      <div v-else-if="details.length === 0" class="state-block">
        <div class="state-icon">
          <font-awesome-icon icon="chart-bar" />
        </div>
        <p class="state-title">No sales data found</p>
        <p class="state-sub">Try adjusting your filters or date range and click Apply.</p>
      </div>

      <template v-else>

        <!-- Toolbar -->
        <div class="table-toolbar">
          <span class="table-count">
            Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong>
            of <strong>{{ pagination.total }}</strong> records
          </span>
          <div class="toolbar-right">
            <label class="table-count">Rows:</label>
            <select v-model="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option :value="pagination.total">All</option>
            </select>
          </div>
        </div>

        <!-- Table -->
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Transactions</th>
                <th class="text-right">Total Amount ({{ company?.currency?.symbol }})</th>
                <th class="text-right">Total Cost ({{ company?.currency?.symbol }})</th>
                <th class="text-right">Total Profit ({{ company?.currency?.symbol }})</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(sale, index) in details" :key="sale.id">
                <td class="text-muted-cell">{{ pagination.from + index }}</td>
                <td class="fw-500">{{ sale.date }}</td>
                <td>
                  <span class="txn-badge">{{ sale.transactions }}</span>
                </td>
                <td class="text-right fw-500">{{ $filters.formatMoney(sale.total_amt) }}</td>
                <td class="text-right text-muted-cell">{{ $filters.formatMoney(sale.total_cost) }}</td>
                <td
                  class="text-right fw-bold"
                  :class="(sale.total_amt - sale.total_cost) < 0 ? 'loss-value' : 'profit-value'"
                >
                  {{ $filters.formatMoney(sale.total_amt - sale.total_cost) }}
                </td>
              </tr>
            </tbody>

            <!-- Grand total row -->
            <tfoot>
              <tr class="grand-total-row">
                <td colspan="2" class="grand-label">Grand Total</td>
                <td>
                  <span class="txn-badge txn-badge--total">{{ total_transaction }}</span>
                </td>
                <td class="text-right fw-bold">{{ $filters.formatMoney(total_amount) }}</td>
                <td class="text-right fw-bold">{{ $filters.formatMoney(total_cost) }}</td>
                <td
                  class="text-right fw-bold"
                  :class="total_profit < 0 ? 'loss-value' : 'profit-value'"
                >
                  {{ $filters.formatMoney(total_profit) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- Pagination -->
        <div class="table-footer">
          <pagination
            :pagination="pagination"
            @prev="tableData.page = pagination.currentPage - 1; fetchData()"
            @next="tableData.page = pagination.currentPage + 1; fetchData()"
          />
        </div>

      </template>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { reusableMixin } from '../../../lib/reusableMixin';
import Multiselect from '@vueform/multiselect';
import ReportHeaderTab from './components/reportheadertab.vue';

export default {
  name: 'DailySalesReport',

  components: { ReportHeaderTab, Multiselect },

  mixins: [reusableMixin],

  data() {
    const today     = new Date();
    const weekAgo   = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);

    return {
      print_content:     [],
      headerArrayColumn: ['S/N', 'Date', 'Transactions', 'Total Amount', 'Total Cost', 'Total Profit'],
      footerRowArray:    [],

      details:      [],
      paymentTypes: [],
      loading:      false,
      crudLoading:  false,
      exportOpen:   false,

      tableData: { page: 1,
        store:        'All',
        payment_type: 'All',
        start_date:   weekAgo,
        end_date:     today,
        draw:         0,
        length:       10,
        search:       '',
        column:       0,
        dir:          'desc',
        status:       [0, 1, 2, 3],
        query:        '',
      },

      pagination: {
        lastPage: '', currentPage: '', total: '',
        lastPageUrl: '', nextPageUrl: '', prevPageUrl: '',
        from: 1, to: '',
      },

      total_transaction: 0,
      total_amount:      0,
      total_cost:        0,
      total_profit:      0,

      toast: useToast(),
    };
  },

  methods: {
    toggleExport() {
      this.exportOpen = !this.exportOpen;
    },

    exportPdf() {
      this.exportOpen = false;
      this.drawPdf(
        this.print_content,
        this.headerArrayColumn,
        'l',
        'daily-sales-report',
        'Daily Sales Report',
        '',
        this.footerRowArray,
      );
    },

    grandTotal(items) {
      this.total_transaction = items.reduce((s, t) => s + t.transactions, 0);
      this.total_amount      = items.reduce((s, t) => s + t.total_amt, 0);
      this.total_cost        = items.reduce((s, t) => parseFloat((parseFloat(s) + parseFloat(t.total_cost)).toFixed(2)), 0);
      this.total_profit      = items.reduce((s, t) => parseFloat((parseFloat(s) + parseFloat(t.total_amt - t.total_cost)).toFixed(2)), 0);

      this.footerRowArray = [
        '', 'GRAND TOTAL', this.total_transaction,
        this.$filters.formatMoney(this.total_amount),
        this.$filters.formatMoney(this.total_cost),
        this.$filters.formatMoney(this.total_profit),
      ];
    },

    dataToPrint(details) {
      this.print_content = details.map((s, idx) => ({
        sn:           idx + 1,
        date:         s.date,
        transactions: s.transactions,
        total_amt:    this.$filters.formatMoney(s.total_amt),
        total_cost:   this.$filters.formatMoney(s.total_cost),
        total_profit: this.$filters.formatMoney(s.total_profit),
      }));
    },

    fetchData(url = '/report/daily-report') {
      this.loading     = true;
      this.crudLoading = true;
      this.tableData.draw++;

      axios.get(url, { params: this.tableData })
        .then(({ data }) => {
          this.details = data.data.sales.data;
          this.grandTotal(data.data.sales2);
          this.dataToPrint(this.details);
          this.configPagination(data.data.sales);
        })
        .catch(() => {
          this.toast.error('Failed to load sales data. Please try again.');
        })
        .finally(() => {
          this.loading     = false;
          this.crudLoading = false;
        });
    },

    configPagination(data) {
      this.pagination.lastPage    = data.last_page;
      this.pagination.currentPage = data.current_page;
      this.pagination.total       = data.total;
      this.pagination.lastPageUrl = data.last_page_url;
      this.pagination.nextPageUrl = data.next_page_url;
      this.pagination.prevPageUrl = data.prev_page_url;
      this.pagination.from        = data.from;
      this.pagination.to          = data.to;
    },
  },

  created() {
    this.fetchData();
    this.allStore();
    this.allPaymentTypes();
    this.getCompany();
  },
};
</script>

<style scoped>
/* ── Page ────────────────────────────────────────────────────────────── */
.report-page { display:flex; flex-direction:column; gap:18px; }

/* ── Header ──────────────────────────────────────────────────────────── */
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }

/* ── Filters ─────────────────────────────────────────────────────────── */
.filters-wrap { display:flex; align-items:flex-end; flex-wrap:wrap; gap:10px; }
.filter-group { display:flex; flex-direction:column; gap:4px; }
.filter-label { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
.filter-sep   { color:#b0bdd0; font-size:16px; margin-bottom:4px; }

.date-input-wrap { position:relative; display:flex; align-items:center; }
.date-icon       { position:absolute; left:10px; color:#7a90b0; font-size:12px; pointer-events:none; z-index:1; }
.date-picker     { background:none; border:none; padding-left:28px; }

.filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; min-width:140px; }
.filter-multiselect { min-width:150px; }

.btn-apply  { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
.btn-apply:hover:not(:disabled) { background:#003b8e; }
.btn-apply:disabled { opacity:.55; cursor:not-allowed; }

.btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
.btn-outline:hover { background:#f4f7fb; }

.dropdown { position:relative; }
.dropdown-menu { position:absolute; top:calc(100% + 6px); right:0; background:#fff; border:1px solid #e4eaf2; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); padding:6px; min-width:160px; z-index:200; display:none; }
.dropdown-menu.show { display:block; }
.dropdown-item { display:flex; align-items:center; gap:8px; padding:8px 12px; font-size:13px; border-radius:7px; color:#1a2b45; cursor:pointer; }
.dropdown-item:hover { background:#f4f7fb; }
.me-2 { margin-right:8px; }

/* ── Summary cards ───────────────────────────────────────────────────── */
.summary-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; }
.summary-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:16px 20px; }
.summary-card--profit { border-color:#bbf7d0; background:#f0fdf4; }
.summary-card--loss   { border-color:#fecaca; background:#fff5f5; }
.summary-label { font-size:12px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; margin-bottom:8px; }
.summary-value { font-size:20px; font-weight:700; color:#1a2b45; }
.summary-card--profit .summary-value { color:#16a34a; }
.summary-card--loss   .summary-value { color:#e03535; }
.profit-arrow { font-size:14px; margin-right:4px; }

/* ── Data card ───────────────────────────────────────────────────────── */
.data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }

/* ── State blocks ────────────────────────────────────────────────────── */
.state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon  { width:60px; height:60px; border-radius:16px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub   { font-size:13px; color:#7a90b0; margin:0; }
.text-muted-cell { color:#4a5e78; }
.mt-3 { margin-top:12px; }

/* ── Table toolbar ───────────────────────────────────────────────────── */
.table-toolbar  { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
.table-count    { font-size:13px; color:#7a90b0; }
.table-count strong { color:#1a2b45; }
.toolbar-right  { display:flex; align-items:center; gap:8px; }
.rows-select    { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }

/* ── Table ───────────────────────────────────────────────────────────── */
.table-wrap   { overflow-x:auto; }
.data-table   { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
.text-right { text-align:right; }
.fw-500  { font-weight:500; }
.fw-bold { font-weight:700; }
.profit-value { color:#16a34a; }
.loss-value   { color:#e03535; }

/* ── Transaction badge ───────────────────────────────────────────────── */
.txn-badge { display:inline-flex; align-items:center; justify-content:center; background:#f4f7fb; color:#1a2b45; border:1px solid #e4eaf2; border-radius:6px; padding:2px 10px; font-size:12px; font-weight:600; }
.txn-badge--total { background:rgba(0,74,173,.08); color:#004AAD; border-color:rgba(0,74,173,.18); }

/* ── Grand total row ─────────────────────────────────────────────────── */
.grand-total-row { background:#f8fafc; border-top:2px solid #e4eaf2; }
.grand-total-row td { padding:13px 16px; font-size:13px; }
.grand-label { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }

/* ── Footer ──────────────────────────────────────────────────────────── */
.table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width:1024px) {
  .summary-grid { grid-template-columns:repeat(2,1fr); }
}
@media (max-width:768px) {
  .page-header   { flex-direction:column; }
  .filters-wrap  { flex-direction:column; align-items:stretch; }
  .filter-sep    { display:none; }
  .summary-grid  { grid-template-columns:repeat(2,1fr); }
  .filter-select, .filter-multiselect { width:100%; }
}
@media (max-width:480px) {
  .summary-grid { grid-template-columns:1fr; }
}
</style>